import React, { createContext, FC, memo, useEffect, useMemo, useState } from 'react';
import { navigate } from '@reach/router';
import { AxiosError } from 'axios';

import { useSafeContext } from 'hooks/useSafeContext/useSafeContext';
import { getCheckoutSession, getPayWidget } from 'services/checkout-service/checkout';
import { isBadRequestError, isNotFoundError } from 'utils/Axios';
import { CheckoutSession, CheckoutType, SettingName, SettingNames, SettingValue } from 'services/checkout-service/models';

import { extractMerchantSettings, mapToCheckoutSession, extractBasePath } from './utils';

export type UsableMerchantSettings = Record<typeof SettingNames[SettingName], SettingValue>;

export type CheckoutSessionState = {
  isLoading: boolean;
  error?: AxiosError ;
  checkoutSession?: CheckoutSession;
  merchantSettings?: UsableMerchantSettings;
};

type ProviderProps = {
  checkoutSessionId?: Uuid;
};

const CheckoutSessionContext = createContext<CheckoutSessionState | undefined>(undefined);
// eslint-disable-next-line
CheckoutSessionContext.displayName = 'CheckoutSession';

export const CheckoutSessionProvider: FC<ProviderProps> = memo(function CheckoutSessionProvider({ children, checkoutSessionId }) {
  const [state, setState] = useState<CheckoutSessionState>({ isLoading: true });

  const isPayWidget = useMemo(() => CheckoutType.PAY_WIDGETS === extractBasePath(window.location.pathname), []);

  useEffect(() => {
    (async () => {
      try {
        let checkoutSession;
        if (isPayWidget) {
          const payWidgetData = await getPayWidget(checkoutSessionId);
          checkoutSession = mapToCheckoutSession(payWidgetData);
        } else {
          checkoutSession = await getCheckoutSession(checkoutSessionId);
        }

        setState({
          isLoading: false,
          checkoutSession,
          merchantSettings: checkoutSession.merchant?.merchantSettings &&
            extractMerchantSettings(checkoutSession.merchant.merchantSettings.elements)
        });
      } catch (error) {
        if (isNotFoundError(error) || isBadRequestError(error)) {
          setState({ isLoading: false });

          return navigate('not-found');
        }

        setState({ isLoading: false, error });
      }
    })();
  }, [checkoutSessionId, isPayWidget]);

  return (
    <CheckoutSessionContext.Provider value={{ ...state }}>
      {children}
    </CheckoutSessionContext.Provider>
  );
});

export function useCheckoutSessionContext() {
  return useSafeContext(useCheckoutSessionContext, CheckoutSessionContext);
}

export function useCheckoutCurrency() {
  const { checkoutSession } = useCheckoutSessionContext();

  return checkoutSession?.merchant?.properties?.defaultCurrency || 'USD';
}

export function useCheckoutLocale() {
  const { checkoutSession } = useCheckoutSessionContext();

  return checkoutSession?.merchant?.properties?.locale || 'en-US';
}
